import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { designer, skills, services, ui } from '../data/mock';
import Footer from '../components/Footer';

const About = () => {
  const refs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => e.isIntersecting && e.target.classList.add('visible'));
    }, { threshold: 0.18 });
    refs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const skillsDoubled = [...skills, ...skills];
  const dotColors = ['#B3543E', '#4A6B82', '#E8B33C', '#3E5F4C'];

  return (
    <main className="min-h-screen w-full" style={{ background: 'var(--bg-dark)' }}>
      {/* Hero with portrait */}
      <section className="pt-40 pb-24 px-8 md:px-[90px]">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="font-label text-[0.72rem] text-[var(--terracotta)] mb-8">
              ————  Om mig  /  Designer
            </div>
            <h1 className="font-display text-[clamp(3rem,9vw,9rem)] text-[var(--cream)] leading-[0.92]">
              En designer<br />
              der <span style={{ fontStyle: 'italic', fontWeight: 300 }}>lytter</span><br />
              til historier.
            </h1>
          </div>
          <div className="md:col-span-5">
            <div className="relative overflow-hidden" style={{ aspectRatio: '3 / 4' }}>
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: 'url(https://images.unsplash.com/photo-1768471125958-78556538fadc)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'brightness(0.82) contrast(1.04) saturate(0.95)'
                }}
              />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 60%, rgba(27,15,13,0.6) 100%)' }} />
              <div className="absolute bottom-5 left-5 font-label text-[0.66rem] text-[var(--cream)]">
                {designer.location.split(',')[0]} — Studio
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bio in Danish */}
      <section className="px-8 md:px-[90px] py-24 border-y border-[var(--cream)]/10">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <div className="font-label text-[0.7rem] text-[var(--amber)]">Manifest — DA</div>
          </div>
          <p
            ref={(el) => (refs.current[0] = el)}
            className="reveal md:col-span-9 font-body text-[clamp(1.3rem,2.2vw,2rem)] text-[var(--cream)]/85 leading-[1.55] max-w-4xl"
          >
            {designer.taglineDA}
          </p>
        </div>
      </section>

      {/* Skills marquee */}
      <section ref={(el) => (refs.current[1] = el)} className="reveal py-24 overflow-hidden">
        <div className="font-label text-[0.7rem] text-[var(--terracotta)] mb-10 px-8 md:px-[90px]">
          Kompetencer  /  Det jeg laver
        </div>
        <div className="relative whitespace-nowrap">
          <div className="marquee-track">
            {skillsDoubled.map((s, i) => (
              <span
                key={`marquee-${i}-${s}`}
                className="font-display text-[clamp(2.5rem,7vw,6rem)] text-[var(--cream)]/80 px-10 inline-block hover:text-[var(--terracotta)] transition-colors duration-500"
                style={i % 3 === 1 ? { fontStyle: 'italic', fontWeight: 300 } : {}}
              >
                {s}<span className="mx-6" style={{ color: dotColors[i % dotColors.length] }}>·</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="px-8 md:px-[90px] py-32">
        <div className="font-label text-[0.7rem] text-[var(--terracotta)] mb-10">
          Ydelser  /  Sådan hjælper jeg
        </div>
        <div className="grid md:grid-cols-2 gap-x-16 gap-y-20">
          {services.map((s, i) => (
            <div
              key={s.id}
              ref={(el) => (refs.current[i + 2] = el)}
              className="reveal"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="flex items-baseline gap-4 mb-5">
                <span className="font-label text-[0.62rem]" style={{ color: s.accent }}>
                  {String(s.id).padStart(2, '0')}
                </span>
                <h3 className="font-display text-3xl md:text-4xl text-[var(--cream)]">
                  {s.title}
                </h3>
              </div>
              <div className="h-px mb-6" style={{ background: `linear-gradient(90deg, ${s.accent} 0%, transparent 60%)` }} />
              <p className="font-body text-[var(--cream)]/60 text-lg leading-relaxed max-w-md">
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="px-8 md:px-[90px] py-32 border-t border-[var(--cream)]/10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-10">
          <h2 className="font-display text-[clamp(2.5rem,6vw,5.5rem)] text-[var(--cream)] leading-[0.95] max-w-3xl">
            Har du en historie<br />
            <span style={{ fontStyle: 'italic', fontWeight: 300, color: 'var(--terracotta)' }}>der skal fortælles?</span>
          </h2>
          <Link to="/contact" className="btn-terra">
            <span>{ui.startProject}</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default About;
